import { Action } from "@mittwald/flow-remote-react-components";
import { Text } from "@mittwald/flow-remote-react-components";
import { Content } from "@mittwald/flow-remote-react-components";
import { Heading } from "@mittwald/flow-remote-react-components";
import { Modal } from "@mittwald/flow-remote-react-components";
import { Section } from "@mittwald/flow-remote-react-components";
import { TextField } from "@mittwald/flow-remote-react-components";
import { Label } from "@mittwald/flow-remote-react-components";
import { ActionGroup } from "@mittwald/flow-remote-react-components";
import { Button } from "@mittwald/flow-remote-react-components";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Form,
  typedField,
} from "@mittwald/flow-remote-react-components/react-hook-form";
import { useCreateUser } from "../../hooks/useCreateUser";
import { useEffect, useState } from "react";
import { CreateUserError } from "../../errors/CreateUserError";
import {
  OverlayController,
  useOverlayController,
} from "@mittwald/flow-remote-react-components";
import { useListUsers } from "../../hooks/useListUsers";
import { ColumnLayout } from "@mittwald/flow-remote-react-components";

interface FormInputs {
  username: string;
  password: string;
}

const CreateUserModal = (props: {
  projectId: string;
  /**
   * Controls the modal from the outside instead of the surrounding
   * ModalTrigger. Lets a parent render the modal outside its own <Form>: the
   * remote renderer does not stop the submit event's propagation, so a modal
   * nested inside a form would submit the outer form as well.
   */
  controller?: OverlayController;
}) => {
  const [errorOnSubmit, setErrorOnSubmit] = useState<Error | undefined>(
    undefined,
  );

  if (errorOnSubmit) {
    throw errorOnSubmit;
  }

  const localController = useOverlayController("Modal", {
    reuseControllerFromContext: props.controller === undefined,
  });
  const controller = props.controller ?? localController;
  const users = useListUsers(props.projectId).data.map((u) => u.username);

  const form = useForm<FormInputs>({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const createUserMutation = useCreateUser(props.projectId);

  const resetCreateUserMutation = createUserMutation.reset;

  // Registered in an effect so the handler is added once and removed on
  // unmount (addOnClose returns the disposer).
  useEffect(
    () =>
      controller.addOnClose(() => {
        form.reset();
        resetCreateUserMutation();
      }),
    [controller, form, resetCreateUserMutation],
  );

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const result = await createUserMutation.mutateAsync({
      username: data.username,
      password: data.password,
    });

    if (result.error) {
      setErrorOnSubmit(new CreateUserError(result.error.message));
    }

    controller.close();
  };

  const Field = typedField(form);

  return (
    <Modal controller={controller} size="s">
      <Form form={form} onSubmit={onSubmit}>
        <Heading>
          <Text>Benutzer anlegen</Text>
        </Heading>
        <Content>
          <Section>
            <Text>
              Den angelegten Benutzer kannst du anschließend für die
              Authentifizierung in jedem gewünschten Passwortschutz nutzen.
            </Text>

            <ColumnLayout m={[1, 1]}>
              <Field
                name={"username"}
                rules={{
                  required: "Bitte gib einen Benutzernamen ein",
                  maxLength: {
                    value: 100,
                    message:
                      "Der Benutzername darf höchstens 100 Zeichen lang sein",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_\-@.]+$/,
                    message:
                      "Erlaubt sind nur Buchstaben, Ziffern und die Zeichen _ - @ .",
                  },
                  validate: {
                    noDuplicateUsernames: (value) =>
                      !users.includes(value)
                        ? true
                        : "Benutzername ist bereits vergeben",
                  },
                }}
              >
                <TextField isRequired autoFocus>
                  <Label>Benutzername</Label>
                </TextField>
              </Field>
              <Field
                name={"password"}
                rules={{
                  required: "Bitte gib ein sicheres Passwort ein",
                }}
              >
                <TextField type="password" isRequired>
                  <Label>Passwort</Label>
                </TextField>
              </Field>
            </ColumnLayout>
          </Section>
        </Content>
        <ActionGroup>
          <Button
            color="success"
            type="submit"
            isPending={createUserMutation.isPending}
            isSucceeded={createUserMutation.isSuccess}
          >
            Benutzer anlegen
          </Button>
          <Action closeOverlay="Modal">
            <Button color="secondary" variant="soft">
              Abbrechen
            </Button>
          </Action>
        </ActionGroup>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
